import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import DatePicker from "@/components/DatePicker";
import {
  Search, Car, Compass, Ticket, Bus, ShieldCheck,
  CreditCard, MapPin, ArrowRight, Star, TrendingUp, Sparkles, KeyRound,
} from "lucide-react";
import FeaturedRentals from "@/components/FeaturedRentals";
import TopTours from "@/components/TopTours";

const CATEGORIES = [
  { name: "Rides", icon: Car },
  { name: "Tours", icon: Compass },
  { name: "Activities", icon: Ticket },
  { name: "Transfers", icon: Bus },
];

const GRID_CATEGORIES = [
  { name: "Rides", icon: Car, to: "/browse?category=Rides" },
  { name: "Tours", icon: Compass, to: "/browse?category=Tours" },
  { name: "Activities", icon: Ticket, to: "/browse?category=Activities" },
  { name: "Transfers", icon: Bus, to: "/browse?category=Transfers" },
  { name: "Car Rentals", icon: KeyRound, to: "/cars", accent: true },
];

const TOP_DESTINATIONS = [
  { name: "Grenada", flag: "gd", tagline: "Spice Isle beaches & waterfalls",
    image: "https://images.unsplash.com/photo-1786027877006-df502ec14431?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { name: "Jamaica", flag: "jm", tagline: "Reggae, reefs & Montego Bay",
    image: "https://images.pexels.com/photos/18030354/pexels-photo-18030354.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { name: "Bahamas", flag: "bs", tagline: "Turquoise cays & powder sand",
    image: "https://images.unsplash.com/photo-1787259295433-eeae01f7060f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { name: "Puerto Rico", flag: "pr", tagline: "Old San Juan & wild coastlines",
    image: "https://images.unsplash.com/photo-1625642471723-12744e6e4211?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
];


const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/51b1277e-a38b-47b7-b47e-12c90aadee88/images/23e1e92e7fd071eb3ff58e0e98910ea0fdc4a89629de8673153df0e77c110af3.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [adventures, setAdventures] = useState([]);
  const [advIsland, setAdvIsland] = useState("");
  const [advLoading, setAdvLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [island, setIsland] = useState("");
  const [category, setCategory] = useState("");
  const [when, setWhen] = useState("");
  const [islands, setIslands] = useState([]);
  const [grenada, setGrenada] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/listings", { params: { featured: true } }).then((r) => { setFeatured(r.data); setAdventures(r.data); }).catch(() => {});
    api.get("/meta").then((r) => { setStats(r.data.stats); setIslands(r.data.islands); }).catch(() => {});
    api.get("/listings", { params: { island: "Grenada", sort: "rating" } }).then((r) => setGrenada(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const selectAdvIsland = (isl) => {
    setAdvIsland(isl);
    if (!isl) { setAdventures(featured); return; }
    setAdvLoading(true);
    api.get("/listings", { params: { island: isl, sort: "rating" } })
      .then((r) => setAdventures(r.data.slice(0, 6)))
      .catch(() => setAdventures([]))
      .finally(() => setAdvLoading(false));
  };

  const search = () => {
    const p = new URLSearchParams();
    if (island) p.set("island", island);
    if (category) p.set("category", category);
    if (when) p.set("date", when);
    navigate(`/browse?${p.toString()}`);
  };

  return (
    <div className="bg-[#060810]">
      <Seo
        title="Carivisor | Caribbean Tours, Excursions & Things to Do"
        description="Discover and book the best Caribbean tours, excursions, activities and unforgettable experiences with trusted local operators. Explore Grenada and destinations across the Caribbean with Carivisor."
        path="/"
      />
      {/* Sticky Viator search bar (stays visible while scrolling) */}
      <div className="sticky top-16 z-40 bg-[#060810]/90 backdrop-blur-md border-b border-[#1D2640]" data-testid="sticky-search">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2.5">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
            <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
            <select data-testid="sticky-island" value={island} onChange={(e) => setIsland(e.target.value)} className="bg-transparent text-white text-sm w-full outline-none cursor-pointer">
              <option value="" className="bg-[#0B0E1B]">Where to?</option>
              {islands.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}
            </select>
          </div>
          <div className="hidden sm:flex flex-1 items-center gap-2 px-3 py-2 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
            <Sparkles className="w-4 h-4 text-emerald-400 shrink-0" />
            <select data-testid="sticky-category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-transparent text-white text-sm w-full outline-none cursor-pointer">
              <option value="" className="bg-[#0B0E1B]">What to do?</option>
              {CATEGORIES.map((c) => <option key={c.name} value={c.name} className="bg-[#0B0E1B]">{c.name}</option>)}
            </select>
          </div>
          <div className="hidden md:block flex-1"><DatePicker testid="sticky-date" value={when} onChange={setWhen} placeholder="When?" /></div>
          <button data-testid="sticky-search-btn" onClick={search} className="btn-neon rounded-xl px-6 py-2.5 font-semibold flex items-center gap-2 shrink-0">
            <Search className="w-4 h-4" /> <span className="hidden sm:inline">Search</span>
          </button>
        </div>
      </div>

      {/* HERO */}
      <section className="relative gradient-hero overflow-hidden">
        <div className="absolute inset-0">
          <video autoPlay muted loop playsInline poster={HERO_IMG} className="w-full h-full object-cover opacity-30">
            <source src="https://videos.pexels.com/video-files/37839118/16050943_640_360_60fps.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#060810]/40 via-[#060810]/70 to-[#060810]" />
        </div>

        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="max-w-3xl fade-up on-dark">
            <span className="inline-flex items-center gap-2 font-mono-caption text-xs uppercase tracking-[0.2em] text-cyan-400 border border-cyan-400/30 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Premium Caribbean Marketplace
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Discover the Best <br />
              <span className="text-gradient-warm">Things to Do</span> in the Caribbean.
            </h1>
            <p className="font-heading text-2xl sm:text-3xl font-extrabold mt-5">
              <span className="text-gradient-warm">See it.</span>{" "}
              <span className="text-gradient-cool">Book it.</span>{" "}
              <span className="text-white">Go.</span>
            </p>
            <p className="text-base md:text-lg text-slate-300 mt-4 max-w-xl leading-relaxed">
              Book unforgettable Caribbean tours, excursions, activities and local experiences with trusted operators across the islands.
            </p>
          </div>

          {/* Viator-style search bar */}
          <div className="relative mt-10 glass neon-border rounded-2xl p-2.5 md:p-3 max-w-5xl fade-up" data-testid="hero-search-bar" style={{ animationDelay: "0.15s" }}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2.5">
              <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
                <MapPin className="w-5 h-5 text-cyan-400 shrink-0" />
                <div className="w-full">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Where to?</p>
                  <select data-testid="hero-island-select" value={island} onChange={(e) => setIsland(e.target.value)} className="bg-transparent text-white text-sm w-full outline-none cursor-pointer">
                    <option value="" className="bg-[#0B0E1B]">Any island</option>
                    {islands.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
                <Sparkles className="w-5 h-5 text-emerald-400 shrink-0" />
                <div className="w-full">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">What to do?</p>
                  <select data-testid="hero-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-transparent text-white text-sm w-full outline-none cursor-pointer">
                    <option value="" className="bg-[#0B0E1B]">Any experience</option>
                    {CATEGORIES.map((c) => <option key={c.name} value={c.name} className="bg-[#0B0E1B]">{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
                <div className="w-full">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 px-1">When?</p>
                  <DatePicker testid="hero-date" value={when} onChange={setWhen} placeholder="Any date" className="!bg-transparent !border-0 !px-1 !py-0.5" />
                </div>
              </div>
              <button data-testid="hero-search-btn" onClick={search} className="btn-neon rounded-xl px-8 py-4 font-bold text-base flex items-center justify-center gap-2">
                <Search className="w-5 h-5" /> Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED ISLAND: GRENADA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto -mt-28 md:-mt-36 relative z-10">
        <div className="relative rounded-3xl overflow-hidden glass neon-border-cool">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1748810859309-b943147cdda2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxHcmVuYWRhJTIwaXNsYW5kJTIwdHVycXVvaXNlJTIwY29hc3QlMjBhZXJpYWx8ZW58MHx8fHwxNzg5MzMzOTQ5fDA&ixlib=rb-4.1.0&q=85"
              alt="Grenada"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070A18] via-[#070A18]/85 to-[#070A18]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-transparent to-transparent" />
          </div>

          <div className="relative p-8 md:p-12">
            <div className="max-w-xl on-dark">
              <span className="inline-flex items-center gap-2 font-mono-caption text-xs uppercase tracking-[0.2em] text-cyan-300 border border-cyan-300/30 rounded-full px-4 py-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Featured Island
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4">
                Discover <span className="text-gradient-cool">Grenada</span>
              </h2>
              <p className="text-slate-300 mt-3 max-w-lg">
                The Spice Isle — fragrant nutmeg estates, hidden rainforest waterfalls, and some of the Caribbean's most vivid turquoise water. Here are the top-rated tours right now.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-8">
              {grenada.map((l, i) => (
                <div key={l.id} className="fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                  <ListingCard listing={l} />
                </div>
              ))}
            </div>

            <Link
              to="/browse?island=Grenada"
              data-testid="grenada-view-all"
              className="inline-flex items-center gap-2 mt-8 btn-cool rounded-full px-6 py-3 font-semibold text-sm"
            >
              Explore all Grenada tours <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TOP TOURS */}
      <TopTours />

      {/* CATEGORIES */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-16 md:pt-24 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {GRID_CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={c.to}
              data-testid={`category-${c.name}`}
              className={`glass glass-hover rounded-2xl p-6 flex flex-col items-center text-center gap-3 ${c.accent ? "neon-border-cool" : ""}`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${c.accent ? "bg-gradient-to-br from-[#ff5e00] to-[#d900ff]" : "bg-[#0B0E1B] border border-[#232A4D]"}`}>
                <c.icon className={`w-7 h-7 ${c.accent ? "text-white" : "text-cyan-300"}`} />
              </div>
              <span className="font-heading font-semibold text-white">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* POPULAR ADVENTURES (Featured escapes) */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-12 md:pt-16 pb-4" data-testid="popular-adventures">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-orange-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Handpicked experiences
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Popular Adventures</h2>
          </div>
          <Link to="/browse" className="hidden md:flex items-center gap-2 text-sm text-cyan-300 hover:text-orange-400 transition-colors">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Island filter chips — swap the carousel in place */}
        {islands.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="adv-island-chips">
            {["", ...islands.slice(0, 7)].map((isl) => {
              const active = advIsland === isl;
              return (
                <button
                  key={isl || "all"}
                  onClick={() => selectAdvIsland(isl)}
                  data-testid={`adv-island-chip-${isl || "all"}`}
                  className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${active ? "bg-cyan-400 text-[#060810] border-cyan-400 font-semibold" : "border-[#1D2640] text-slate-300 hover:text-white hover:border-cyan-400/40"}`}
                >
                  {isl || "All islands"}
                </button>
              );
            })}
          </div>
        )}

        {advLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[0, 1, 2].map((i) => <div key={i} className="h-72 rounded-2xl bg-[#0B0E1B] border border-[#1D2640] animate-pulse" />)}
          </div>
        ) : adventures.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-slate-400" data-testid="adv-empty">
            No adventures in {advIsland} yet — <button onClick={() => selectAdvIsland("")} className="text-cyan-300 hover:text-orange-400">show all islands</button>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {adventures.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        )}
      </section>

      {/* FEATURED RENTALS */}
      <FeaturedRentals />

      {/* TOP DESTINATIONS */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-16 md:pt-24" data-testid="top-destinations">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-orange-400 flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4" /> Top destinations
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Discover Your Next Caribbean Escape</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {TOP_DESTINATIONS.map((d) => (
            <Link
              key={d.name}
              to={`/browse?island=${encodeURIComponent(d.name)}`}
              data-testid={`destination-${d.name}`}
              className="group relative h-72 md:h-80 rounded-3xl overflow-hidden neon-border-cool"
            >
              <img
                src={d.image}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <img
                src={`https://flagcdn.com/w80/${d.flag}.png`}
                srcSet={`https://flagcdn.com/w160/${d.flag}.png 2x`}
                alt={`${d.name} flag`}
                className="absolute top-4 right-4 w-11 h-auto rounded-md ring-2 ring-white/70 shadow-lg"
              />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-heading text-xl font-bold text-white drop-shadow">{d.name}</h3>
                <p className="text-sm text-slate-200/90 mt-0.5">{d.tagline}</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-cyan-300 group-hover:text-orange-400 transition-colors">
                  Explore {d.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS */}
      {stats && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-14 md:pt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 fade-up">
            {[
              { label: "Curated listings", value: stats.listings },
              { label: "Local hosts", value: stats.providers },
              { label: "Islands", value: stats.islands },
              { label: "Trips booked", value: stats.bookings },
            ].map((s) => (
              <div key={s.label} className="glass rounded-xl px-4 py-4 text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold neon-text-cyan">{s.value}+</div>
                <div className="text-xs text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section id="how" className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-emerald-400">Simple by design</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">How Carivisor works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { icon: Search, title: "Discover", desc: "Filter by island, category, and price to find the perfect escape from vetted local hosts." },
            { icon: CreditCard, title: "Book securely", desc: "Reserve instantly with encrypted Stripe checkout. Transparent pricing, no surprises." },
            { icon: ShieldCheck, title: "Travel with confidence", desc: "Every host is verified and every booking is protected. Manage trips from your dashboard." },
          ].map((s, i) => (
            <div key={s.title} className="glass rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -top-6 -right-4 font-heading text-8xl font-black text-white/5">{i + 1}</div>
              <div className="w-12 h-12 rounded-xl btn-neon flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROVIDER CTA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-8">
        <div className="relative glass neon-border rounded-3xl overflow-hidden p-8 md:p-14">
          <div className="absolute inset-0 opacity-20">
            <img src="https://images.unsplash.com/photo-1769610352818-cf8fa29ccf9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB5YWNodCUyMGNhdGFtYXJhbiUyMGNhcmliYmVhbiUyMHN1bnNldHxlbnwwfHx8fDE3ODkyNzA3MDR8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative max-w-2xl">
            <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-violet-400">For hosts</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3">
              List your tour, ride, or experience.
            </h2>
            <p className="text-slate-300 mt-4 max-w-lg">
              Reach high-intent travelers, manage bookings effortlessly, and keep <span className="text-emerald-400 font-semibold">80% of every booking</span> — Carivisor's flat 20% commission covers payments, support, and reach.
            </p>
            <Link
              to="/register"
              data-testid="become-host-cta"
              className="inline-flex items-center gap-2 mt-7 btn-neon rounded-full px-7 py-3 font-semibold"
            >
              Become a host <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
